import { LINEAR_GRADIENTS } from "@src/features/contentDetail/ui/CoverImage";
import { useOpenGraphDataQuery } from "@src/features/contentDetail/ui/MediaPreview.generated";
import VolumeUpOutlineIcon from "@src/lib/icons/18px/volume-up-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import MediaPlayFillIcon from "@src/lib/icons/18px/media-play-fill";
import { hashStringToNumber } from "@src/lib/ui/Pill";
import Text from "@src/lib/ui/Text";
import { useMuxPreviewUrl } from "@src/lib/hooks/useMuxPreviewUrl";
import { BackgroundGradient, LibraryItemType } from "@src/types.generated";
import VideoPlayer from "@src/lib/ui/VideoPlayer";
import { getLinkType, isWebLink, LinkType } from "@src/lib/utils/files";
import { CSSProperties, FC, useMemo } from "react";
import { cn } from "@src/lib/utils/cn";
import { OgData } from "@src/features/libraryItem/course/OpenGraphLinkPreview";
import {
  CoverImageFragment,
  ImageUrlsFragment,
} from "@src/fragments.generated";
import VideoOutlineIcon from "@src/lib/icons/18px/video-outline";
import ContentTypeIcon from "./contentTypeIcons";

export type ContentAvatarSize =
  | "18px"
  | "24px"
  | "28px"
  | "32px"
  | "40px"
  | "56px"
  | "64px"
  | "88px"
  | number;

export type ContentType =
  | "Course"
  | "Module"
  | "Skill"
  | "Resource"
  | "PremiumModule"
  | "Task"
  | "Audit"
  | "SCORMCourse"
  | "Folder";

export const LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE: Record<
  LibraryItemType,
  ContentType
> = {
  [LibraryItemType.Course]: "Course",
  [LibraryItemType.Module]: "Module",
  [LibraryItemType.Skill]: "Skill",
  [LibraryItemType.TrainingResource]: "Resource",
  [LibraryItemType.PremiumContent]: "PremiumModule",
  [LibraryItemType.Sharp]: "PremiumModule",
  [LibraryItemType.Task]: "Task",
  [LibraryItemType.Audit]: "Audit",
  [LibraryItemType.ScormCourse]: "SCORMCourse",
  [LibraryItemType.Folder]: "Folder",
};

export const getLibraryItemTypeCopy = (): Record<LibraryItemType, string> => ({
  [LibraryItemType.Course]: "Course",
  [LibraryItemType.Module]: "Module",
  [LibraryItemType.Skill]: "Check-in",
  [LibraryItemType.TrainingResource]: "Resource",
  [LibraryItemType.PremiumContent]: "Premium Module",
  [LibraryItemType.Sharp]: "Premium Module",
  [LibraryItemType.Task]: "Checklist",
  [LibraryItemType.Audit]: "Audit",
  [LibraryItemType.ScormCourse]: "SCORM Course",
  [LibraryItemType.Folder]: "Folder",
});

export const useLibraryItemTypeCopy = (): Record<LibraryItemType, string> =>
  getLibraryItemTypeCopy();

export const CONTENT_TYPE_COPY: Record<ContentType, string> = {
  Course: "Course",
  Module: "Module",
  Skill: "Check-in",
  Resource: "Resource",
  PremiumModule: "Premium Module",
  Task: "Checklist",
  Audit: "Audit",
  SCORMCourse: "SCORM Course",
  Folder: "Folder",
};

// This is a complicated way of saying "ids are optional"
export type CoverImage_ContentAvatar = Omit<
  CoverImageFragment,
  "id" | "imageUrls"
> & {
  id?: string;
  imageUrls?:
    | (Omit<ImageUrlsFragment, "id"> & {
        id?: string;
      })
    | null;
};

type MediaUrlsTranslationSet = {
  en?: string;
  myTranslation?: string;
};

export type ContentAvatarMedia = {
  thumbnailImageUrl?: string | null;
  mediaUrls?: MediaUrlsTranslationSet;
};

type Props = {
  defaultBackgroundHashKey: string | number;
  size?: ContentAvatarSize;
  contentType: ContentType;
  coverImage?: CoverImage_ContentAvatar | null;
  media?: ContentAvatarMedia | null;
  iconSize?: number;
  fullSizeImage?: boolean;
  className?: string;
  style?: CSSProperties;
};

const ContentAvatar: FC<Props> = ({
  defaultBackgroundHashKey,
  size = "40px",
  contentType,
  coverImage,
  fullSizeImage = false,
  className,
  style,
  iconSize = 20,
  media,
}) => {
  const backgroundGradient: BackgroundGradient = useMemo(() => {
    if (coverImage && coverImage.background) {
      return coverImage.background;
    }
    const hashedNumberId = hashStringToNumber(String(defaultBackgroundHashKey));
    const gradients: BackgroundGradient[] = Object.values(BackgroundGradient);
    return gradients[hashedNumberId % gradients.length];
  }, [defaultBackgroundHashKey, coverImage]);
  const emojiIcon: string | null | undefined =
    coverImage && coverImage.emojiIcon;
  const url = useMemo(() => {
    if (media?.thumbnailImageUrl) {
      return media.thumbnailImageUrl;
    }
    if (fullSizeImage) {
      return coverImage?.imageUrls?.medium || coverImage?.imageUrls?.original;
    }
    return (
      coverImage?.imageUrls?.thumb ||
      coverImage?.imageUrls?.medium ||
      coverImage?.imageUrls?.original ||
      media?.mediaUrls?.myTranslation ||
      media?.mediaUrls?.en
    );
  }, [fullSizeImage, coverImage, media]);
  const linkType = useMemo(() => {
    if (url) {
      return getLinkType(url);
    }
    return null;
  }, [url]);
  const { data: ogData } = useOpenGraphDataQuery({
    skip: !url,
    variables: { url: url || "" },
  });
  const shouldAttemptToDisplayPreview = useMemo(() => {
    return (
      (!!linkType &&
        [
          LinkType.IMAGE,
          LinkType.GIF,
          LinkType.MUX_VIDEO,
          LinkType.VIDEO,
          LinkType.HOSTED_VIDEO,
        ].includes(linkType)) ||
      // A web link reads as one — its page's image, or the link glyph when
      // it has none — rather than as a generic document. Uploaded files that
      // getLinkType also calls links (Word, spreadsheets) keep their icon.
      // An emoji cover still wins.
      (linkType === LinkType.LINK && !!url && !emojiIcon && isWebLink(url)) ||
      (!!ogData?.OpenGraphData && ogData.OpenGraphData["og:image"])
    );
  }, [linkType, url, emojiIcon, ogData?.OpenGraphData]);

  const gradientStyle = useMemo(() => {
    if (!backgroundGradient) return undefined;
    return {
      background: LINEAR_GRADIENTS[backgroundGradient],
    };
  }, [backgroundGradient]);

  const hasFullSizeClass =
    className?.includes("size-full") ||
    className?.includes("w-full") ||
    className?.includes("h-full");

  return (
    <div
      className={cn(
        "relative overflow-hidden select-none rounded-md bg-gray-4-alpha",
        className,
      )}
      style={{
        ...(hasFullSizeClass
          ? {}
          : { width: size, height: size, minWidth: size }),
        ...style,
      }}
    >
      {shouldAttemptToDisplayPreview && (
        <MediaThumbnail
          url={url || ""}
          alt={url || ""}
          ogData={ogData?.OpenGraphData as OgData | undefined}
          size={size}
          className={cn("rounded border-none outline-none p-0", className)}
          style={style}
        />
      )}
      {!shouldAttemptToDisplayPreview && (
        <div
          className={cn(
            "absolute inset-0 z-[2] flex items-center justify-center overflow-hidden rounded-t text-white",
            className,
          )}
          style={{
            ...gradientStyle,
            ...style,
          }}
        >
          {emojiIcon ? (
            <div className="flex flex-1 items-center justify-center self-stretch">
              <Text variant="body-description">{emojiIcon}</Text>
            </div>
          ) : (
            <ContentTypeIcon
              contentType={contentType}
              width={iconSize}
              height={iconSize}
            />
          )}
        </div>
      )}
    </div>
  );
};

export function getResourceIcon(url: string): React.ReactNode {
  const linkType = getLinkType(url);
  if (linkType == LinkType.AUDIO) {
    return <VolumeUpOutlineIcon className="size-4" />;
  }
  return <LinkIcon className="size-4" />;
}

type MediaThumbnailProps = {
  url: string;
  alt?: string;
  className?: string;
  iconSize?: number;
  size?: string | number;
  style?: CSSProperties;
  ogData?: OgData;
  videoIconClassName?: string;
};

export const MediaThumbnail: FC<MediaThumbnailProps> = ({
  url,
  className,
  alt,
  iconSize,
  size = "40px",
  style,
  ogData,
  videoIconClassName,
}) => {
  const linkType = getLinkType(url);
  const muxPreviewUrl = useMuxPreviewUrl(url);
  const hasFullSizeClass =
    className?.includes("size-full") ||
    className?.includes("w-full") ||
    className?.includes("h-full");
  const baseStyles: CSSProperties = {
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 8,
    objectFit: "cover",
    ...(hasFullSizeClass ? {} : { width: size, height: size }),
  };
  if ([LinkType.IMAGE, LinkType.GIF, LinkType.MUX_VIDEO].includes(linkType)) {
    let formattedUrl = url;
    if (linkType == LinkType.MUX_VIDEO) {
      if (muxPreviewUrl === undefined) {
        return (
          <div
            className={cn(
              "overflow-hidden flex items-center justify-center rounded-lg object-cover bg-gray-4-alpha [&::-webkit-media-controls]:hidden",
              className,
            )}
            style={{
              ...baseStyles,
              ...style,
            }}
          >
            <VideoOutlineIcon className="text-gray-10" />
          </div>
        );
      }
      formattedUrl = muxPreviewUrl;
    }
    return (
      <img
        className={cn(
          "overflow-hidden flex items-center justify-center rounded-lg object-cover bg-gray-4-alpha [&::-webkit-media-controls]:hidden",
          className,
        )}
        style={{ ...baseStyles, ...style }}
        src={formattedUrl}
        alt={alt}
      />
    );
  } else if (linkType === LinkType.VIDEO) {
    return (
      <div
        className={cn("relative", hasFullSizeClass && "size-full")}
        style={
          hasFullSizeClass ? style : { width: size, height: size, ...style }
        }
      >
        <VideoPlayer
          videoProps={{
            className: cn(
              "overflow-hidden flex items-center justify-center rounded-lg object-cover bg-gray-4-alpha [&::-webkit-media-controls]:hidden",
              className,
            ),
            style: { ...baseStyles, ...style },
            loop: true,
            muted: true,
          }}
          src={url}
        />
        <VideoIndicatorOverlay iconClassName={videoIconClassName} />
      </div>
    );
  } else if (linkType === LinkType.PDF) {
    return (
      <div
        className={cn(
          "overflow-hidden flex items-center justify-center rounded-lg object-cover bg-gray-4-alpha [&::-webkit-media-controls]:hidden",
          className,
        )}
        style={{ ...baseStyles, ...style }}
      >
        <Text variant="fine-print-title" className="text-gray-7">
          PDF
        </Text>
      </div>
    );
  } else {
    return (
      <OGThumbnail
        size={size}
        url={url}
        ogData={ogData}
        iconSize={iconSize}
        className={className}
        style={style}
      />
    );
  }
};

type OGThumbnailProps = {
  url: string;
  className?: string;
  iconSize?: number;
  size: string | number;
  style?: CSSProperties;
  ogData?: OgData;
};

const OGThumbnail: FC<OGThumbnailProps> = ({
  url,
  className,
  size,
  style,
  ogData,
}) => {
  const hasFullSizeClass =
    className?.includes("size-full") ||
    className?.includes("w-full") ||
    className?.includes("h-full");
  let thumbnailUrl = null;
  let hasVideoData = false;
  if (ogData) {
    if (ogData["og:image"]) {
      thumbnailUrl = ogData["og:image"];
    }
    hasVideoData = !!ogData["og:video:url"];
  }
  if (thumbnailUrl === null) {
    return (
      <div
        className={cn(
          "overflow-hidden flex items-center justify-center rounded-lg object-cover bg-gray-4-alpha [&::-webkit-media-controls]:hidden",
          className,
        )}
        style={hasFullSizeClass ? {} : { width: size, height: size }}
      >
        <span className="flex items-center justify-center text-gray-10">
          {getResourceIcon(url)}
        </span>
      </div>
    );
  }
  return (
    <div
      className={cn("relative", hasFullSizeClass && "size-full")}
      style={hasFullSizeClass ? style : { width: size, height: size, ...style }}
    >
      <img
        className={cn(
          "overflow-hidden object-cover [&::-webkit-media-controls]:hidden rounded",
          className,
        )}
        style={
          hasFullSizeClass ? style : { width: size, height: size, ...style }
        }
        src={thumbnailUrl}
        alt={url}
      />
      {hasVideoData && <VideoIndicatorOverlay />}
    </div>
  );
};

const MediaIndicatorOverlay: FC<{
  icon: React.ReactNode;
  style?: React.CSSProperties;
}> = (props) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-[18px] w-[22px] rounded-md bg-black opacity-50" />
      </div>
      <span
        className="z-[1] flex items-center justify-center text-white"
        style={props.style}
      >
        {props.icon}
      </span>
    </div>
  );
};

export const VideoIndicatorOverlay: FC<{ iconClassName?: string }> = ({
  iconClassName,
}) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <MediaPlayFillIcon
        className={cn(
          "size-4 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]",
          iconClassName,
        )}
      />
    </div>
  );
};

export const LinkIndicatorOverlay: FC = () => {
  return <MediaIndicatorOverlay icon={<LinkIcon className="size-4" />} />;
};

export default ContentAvatar;
